import { useEffect } from "react";
import { PHONE_DISPLAY, LOCATION, SITE_URL, ADDRESS, EMAIL, SOCIAL } from "../../lib/site";

const BUSINESS_NAME = "PRYME EPOXY";
const ORG_ID = `${SITE_URL}/#organization`;

function setMeta(key, content, attr = "name") {
  if (content == null) return;
  let el = document.head.querySelector(`meta[${attr}="${key}"]`);
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(attr, key);
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
}

function setLink(rel, href) {
  let el = document.head.querySelector(`link[rel="${rel}"]`);
  if (!el) {
    el = document.createElement("link");
    el.setAttribute("rel", rel);
    document.head.appendChild(el);
  }
  el.setAttribute("href", href);
}

function setJsonLd(id, data) {
  let el = document.getElementById(id);
  if (!el) {
    el = document.createElement("script");
    el.type = "application/ld+json";
    el.id = id;
    document.head.appendChild(el);
  }
  el.textContent = JSON.stringify(data);
}

// Reusable SEO head manager: title, meta, canonical and JSON-LD schema.
const Seo = ({ title, description, path = "/", jsonLd, jsonLdId = "page-schema", robots }) => {
  useEffect(() => {
    if (title) document.title = title;
    if (robots) {
      setMeta("robots", robots);
    } else {
      const existing = document.head.querySelector('meta[name="robots"]');
      if (existing) existing.remove();
    }
    if (description) {
      setMeta("description", description);
      setMeta("og:description", description, "property");
      setMeta("twitter:description", description);
    }
    if (title) {
      setMeta("og:title", title, "property");
      setMeta("twitter:title", title);
    }
    setMeta("og:type", "website", "property");
    setMeta("og:site_name", BUSINESS_NAME, "property");
    const url = `${SITE_URL}${path}`;
    setMeta("og:url", url, "property");
    setLink("canonical", url);

    if (jsonLd) setJsonLd(jsonLdId, jsonLd);
  }, [title, description, path, jsonLd, jsonLdId, robots]);

  return null;
};

// Schema builders shared across pages.
const PRIMARY_MARKETS = ["Prescott", "Flagstaff", "Sedona", "Prescott Valley"];
const OTHER_AREAS = ["Chino Valley", "Cottonwood", "Camp Verde", "Dewey-Humboldt", "Mayer", "Williams", "Kingman", "Payson", "Show Low"];

// Canonical business entity — reused via @id across the site.
export const organizationSchema = () => ({
  "@context": "https://schema.org",
  "@type": ["GeneralContractor", "HomeAndConstructionBusiness", "LocalBusiness"],
  "@id": ORG_ID,
  name: BUSINESS_NAME,
  description:
    "PRYME EPOXY is a licensed, bonded and insured concrete coating contractor based in Mayer, Arizona, installing residential garage floor coatings — including flake and polyaspartic systems — plus epoxy, patio, commercial and industrial concrete coatings for homeowners and businesses throughout Northern Arizona.",
  url: SITE_URL,
  logo: `${SITE_URL}/images/logo.png`,
  image: `${SITE_URL}/images/flake-truck-garage.jpg`,
  telephone: PHONE_DISPLAY,
  email: EMAIL,
  priceRange: "$$",
  address: {
    "@type": "PostalAddress",
    streetAddress: ADDRESS.street,
    addressLocality: ADDRESS.city,
    addressRegion: ADDRESS.region,
    postalCode: ADDRESS.postalCode,
    addressCountry: ADDRESS.country,
  },
  areaServed: [...PRIMARY_MARKETS, ...OTHER_AREAS].map((c) => ({ "@type": "City", name: `${c}, AZ` }))
    .concat([{ "@type": "AdministrativeArea", name: "Northern Arizona" }]),
  openingHoursSpecification: [{
    "@type": "OpeningHoursSpecification",
    dayOfWeek: ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"],
    opens: "07:00",
    closes: "18:00",
  }],
  knowsAbout: [
    "Garage floor coatings", "Polyaspartic coatings", "Epoxy coatings", "Flake flooring",
    "Patio coatings", "Commercial concrete coatings", "Concrete grind and seal", "Polished concrete",
  ],
  sameAs: [SOCIAL.instagram, SOCIAL.facebook],
});

// Kept for backward compatibility; now returns the full organization entity.
export const localBusinessSchema = () => organizationSchema();

export const faqSchema = (faqs) => ({
  "@context": "https://schema.org",
  "@type": "FAQPage",
  mainEntity: faqs.map((f) => ({
    "@type": "Question",
    name: f.q,
    acceptedAnswer: { "@type": "Answer", text: f.a },
  })),
});

export const serviceSchema = (name, description) => ({
  "@context": "https://schema.org",
  "@type": "Service",
  serviceType: name,
  provider: { "@id": ORG_ID },
  areaServed: [...PRIMARY_MARKETS].map((c) => ({ "@type": "City", name: `${c}, AZ` }))
    .concat([{ "@type": "AdministrativeArea", name: "Northern Arizona" }]),
  description,
});

export const breadcrumbSchema = (items) => ({
  "@context": "https://schema.org",
  "@type": "BreadcrumbList",
  itemListElement: items.map((it, i) => ({
    "@type": "ListItem",
    position: i + 1,
    name: it.name,
    item: `${SITE_URL}${it.path}`,
  })),
});

export const reviewSchema = (reviews) => ({
  "@context": "https://schema.org",
  "@type": "LocalBusiness",
  name: BUSINESS_NAME,
  review: reviews.map((r) => ({
    "@type": "Review",
    author: { "@type": "Person", name: r.name },
    reviewRating: { "@type": "Rating", ratingValue: "5", bestRating: "5" },
    reviewBody: r.text,
  })),
});

export const _location = LOCATION;
export default Seo;
